import React from "react";
import CurrentWeather from "./CurrentWeather";
import ForecastSection from "./ForecastSection";
import PrecipitationSection from "./PrecipitationSection";
import ComparisonSection from "./ComparisonSection";
import DatePicker from "./DatePicker";
import Accordion from "./Accordion";
import {
  getPrecipitationType,
  getWeeklyPrecipitationType,
} from "../utils/weatherUtils";

// Import weather components
import HumidityGauge from "./weather/HumidityGauge";
import WindIndicator from "./weather/WindIndicator";
import AirQuality from "./weather/AirQuality";
import UVIndex from "./weather/UVIndex";
import PressureTrend from "./weather/PressureTrend";
import VisibilityRange from "./weather/VisibilityRange";
import TemperatureDetails from "./weather/TemperatureDetails";
import SunTimeline from "./weather/SunTimeline";
import WeatherAlerts from "./weather/WeatherAlerts";
import TemperatureChart from "./TemperatureChart";
import PrecipitationChart from "./PrecipitationChart";
import MoonTimeline from "./weather/moonTimeline";
import WeatherHistory from "./WeatherHistory/WeatherHistory";

export const WeatherDashboard = ({
  currentWeather,
  forecast,
  processedData,
  selectedDate,
  onDateChange,
}) => {
  // Get the weather data for the selected date
  const selectedDateData = processedData.dailyForecastArray.find(
    (data) =>
      new Date(data.dt * 1000).toDateString() === selectedDate.toDateString()
  );

  // For current day, use currentWeather, for other days use selectedDateData
  const isCurrentDay =
    new Date().toDateString() === selectedDate.toDateString();
  const displayData = isCurrentDay ? currentWeather : selectedDateData;

  // Get forecast data for the selected date
  const selectedDateForecast = forecast.filter(
    (item) =>
      new Date(item.dt * 1000).toDateString() === selectedDate.toDateString()
  );

  // Calculate precipitation for the selected date
  const calculatePrecipitation = (data) => {
    return (
      (data?.rain?.["1h"] || 0) +
      (data?.rain?.["3h"] || 0) +
      (data?.snow?.["1h"] || 0) +
      (data?.snow?.["3h"] || 0)
    );
  };

  const precipitationData = {
    last24h: {
      amount: calculatePrecipitation(displayData),
      type: getPrecipitationType(
        displayData?.weather?.[0]?.main,
        displayData?.weather?.[0]?.description
      ),
    },
    next24h: {
      amount: selectedDateForecast?.[0]
        ? calculatePrecipitation(selectedDateForecast[0])
        : 0,
      type: getPrecipitationType(
        selectedDateForecast?.[0]?.weather?.[0]?.main,
        selectedDateForecast?.[0]?.weather?.[0]?.description
      ),
    },
    nextWeek: {
      amount: forecast
        ?.slice(0, 7)
        .reduce((acc, f) => acc + calculatePrecipitation(f), 0),
      type: getWeeklyPrecipitationType(forecast?.slice(0, 7)),
    },
  };

  return (
    <main className="container mx-auto px-4 py-6 pr-8">
      {/* Current Weather Section */}
      <div id="current-weather" className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <CurrentWeather data={displayData} />
          <DatePicker selectedDate={selectedDate} onDateChange={onDateChange} />
        </div>
      </div>

      {/* Weather Alerts */}
      <div id="weather-alerts" className="mb-4">
        <Accordion
          title="Weather Alerts"
          defaultOpen={
            !!displayData?.alerts && Object.keys(displayData.alerts).length > 0
          }
        >
          <WeatherAlerts alerts={displayData?.alerts} />
        </Accordion>
      </div>

      {/* Main Weather Section */}
      <div
        id="main-weather"
        className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4"
      >
        <Accordion title="Forecast">
          <ForecastSection
            forecast={forecast}
            dailyForecastArray={processedData.dailyForecastArray}
          />
        </Accordion>

        <Accordion title="Precipitation">
          <PrecipitationSection data={precipitationData} />
        </Accordion>

        <Accordion title="Temperature Comparison">
          <ComparisonSection
            today={{
              min: displayData?.main?.temp_min,
              max: displayData?.main?.temp_max,
            }}
            yesterday={processedData.yesterday}
            tomorrow={processedData.tomorrow}
          />
        </Accordion>

     
      </div>
      <div id="detailed-weather" className="space-y-4 mb-4">

   {/* Weather History Section */}
      <div id="weather-history" className="mb-4">
        <Accordion title="Weather History">
          <WeatherHistory
            lat={displayData?.coord?.lat || displayData?.lat}
            lon={displayData?.coord?.lon || displayData?.lon}
            todayCondition={displayData?.weather?.[0]?.main}
            cityName={displayData?.name}
          />
        </Accordion>
      </div>
      </div>
      {/* Detailed Weather Section */}
      <div id="detailed-weather" className="space-y-4 mb-4">
        {/* First row - 3 equal height components */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Accordion title="Humidity">
            <HumidityGauge humidity={displayData?.main?.humidity} />
          </Accordion>

          <Accordion title="Wind">
            <WindIndicator
              speed={displayData?.wind?.speed}
              direction={displayData?.wind?.deg}
              gust={displayData?.wind?.gust}
            />
          </Accordion>

          <Accordion title="UV Index">
            <UVIndex value={displayData?.uvi} />
          </Accordion>
        </div>

        {/* Second row - Air Quality takes full width */}
        <div className="w-full">
          <Accordion title="Air Quality">
            <AirQuality
              aqi={displayData?.airQuality?.aqi}
              pm25={displayData?.airQuality?.pm25}
              pm10={displayData?.airQuality?.pm10}
              co={displayData?.airQuality?.co}
              no2={displayData?.airQuality?.no2}
              o3={displayData?.airQuality?.o3}
              so2={displayData?.airQuality?.so2}
            />
          </Accordion>
        </div>
      </div>

      {/* Additional Weather Section */}
      <div
        id="additional-weather"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4"
      >
        <Accordion title="Pressure Trend">
          <PressureTrend
            pressureData={selectedDateForecast.map((item) => ({
              timestamp: item.dt,
              pressure: item.main.pressure,
            }))}
          />
        </Accordion>

        <Accordion title="Visibility">
          <VisibilityRange
            visibility={displayData?.visibility}
            weatherCondition={displayData?.weather?.[0]?.description}
          />
        </Accordion>

        <Accordion title="Temperature Details">
          <TemperatureDetails
            actualTemp={displayData?.main?.temp}
            feelsLike={displayData?.main?.feels_like}
            humidity={displayData?.main?.humidity}
          />
        </Accordion>
      </div>

      {/* Sun Timeline Section */}
      <div id="sun-timeline" className="mb-4">
        <Accordion title="Sun Timeline">
          <SunTimeline
            sunrise={displayData?.sys?.sunrise}
            sunset={displayData?.sys?.sunset}
            currentTime={selectedDate.getTime() / 1000}
          />
        </Accordion>
      </div>

      {/* Moon Timeline Section */}
      <div id="moon-timeline" className="mb-4">
        <Accordion title="Moon Timeline">
          <MoonTimeline
            moonrise={displayData?.moonrise}
            moonset={displayData?.moonset}
            moonPhase={displayData?.moonPhase}
          />
        </Accordion>
      </div>

      {/* Charts Section */}
      <div id="charts" className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Accordion title="Temperature Chart">
          <TemperatureChart hourlyData={forecast} selectedDate={selectedDate} />
        </Accordion>
        <Accordion title="Precipitation Chart">
          <PrecipitationChart
            hourlyData={forecast}
            selectedDate={selectedDate}
          />
        </Accordion>
      </div>
    </main>
  );
};
